import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Building2, TrendingUp, Banknote, CreditCard, Smartphone,
  Clock, ArrowUpDown, Users2, ShieldCheck, RefreshCw, Radio, ArrowRight, PlusCircle,
  BookMarked,
} from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { AppHeader } from '../components/AppHeader';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { monthlySummary, reportTrend } from '../lib/api';
import { formatRp, MONTHS_ID } from '../lib/format';

const now = new Date();

const HeroKpi = ({ icon: Icon, label, value, accent = 'emerald', hint, testid }) => {
  const colors = {
    emerald: 'from-emerald-800 to-emerald-950 text-lime-200 border-emerald-800',
    sky: 'from-sky-700 to-sky-900 text-sky-100 border-sky-700',
    amber: 'from-amber-600 to-orange-700 text-amber-100 border-amber-600',
    rose: 'from-rose-700 to-rose-900 text-rose-100 border-rose-700',
    stone: 'from-stone-700 to-stone-900 text-stone-200 border-stone-700',
  };
  return (
    <div
      className={`relative overflow-hidden rounded-xl border p-5 text-white bg-gradient-to-br ${colors[accent]} card-lift transition-transform`}
      data-testid={testid}
    >
      <div className="absolute inset-0 dot-grid opacity-30" />
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="w-10 h-10 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center backdrop-blur">
            <Icon className="w-5 h-5" />
          </div>
          {hint && (
            <span className="text-[10px] uppercase tracking-widest opacity-70">{hint}</span>
          )}
        </div>
        <div className="text-[11px] uppercase tracking-widest opacity-80 mb-1">{label}</div>
        <div className="num text-xl font-semibold">{value}</div>
      </div>
    </div>
  );
};

const Sparkline = ({ data, color }) => (
  <ResponsiveContainer width="100%" height={44}>
    <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
      <defs>
        <linearGradient id={`grad-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.35} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <Tooltip
        cursor={{ stroke: color, strokeWidth: 1, strokeDasharray: '3 3' }}
        contentStyle={{
          background: 'white',
          border: '1px solid #e7e5e4',
          borderRadius: 6,
          fontSize: 11,
          padding: '4px 8px',
        }}
        formatter={(v) => [formatRp(v), 'Tunai']}
        labelFormatter={(l) => `Tgl ${l}`}
      />
      <Area
        type="monotone"
        dataKey="tunai"
        stroke={color}
        strokeWidth={1.75}
        fill={`url(#grad-${color})`}
      />
    </AreaChart>
  </ResponsiveContainer>
);

export default function AdminPage() {
  const navigate = useNavigate();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState({ branches: [] });
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [justUpdated, setJustUpdated] = useState(false);
  const [trend, setTrend] = useState([]);
  const prevSignatureRef = useRef('');

  const POLL_INTERVAL_MS = 20000;

  const buildSignature = (payload) =>
    (payload?.branches || [])
      .map((b) => `${b.branch_id}:${b.days_recorded}:${b.total_tunai}:${b.total_debit}:${b.total_emoney}:${b.total_pending}:${b.total_selisih}:${b.unbalanced_count}`)
      .join('|');

  const fetchData = useCallback(async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    try {
      const r = await monthlySummary(year, month);
      const nextSig = buildSignature(r);
      if (nextSig !== prevSignatureRef.current) {
        prevSignatureRef.current = nextSig;
        setData(r);
        if (isBackground) {
          setJustUpdated(true);
          setTimeout(() => setJustUpdated(false), 1500);
        } else {
          setData(r);
        }
      }
      setLastUpdated(new Date());
    } catch (_) {
      /* ignore polling errors silently */
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [year, month]);

  // Initial fetch + on filter change
  useEffect(() => {
    prevSignatureRef.current = '';
    fetchData(false);
  }, [year, month, fetchData]);

  useEffect(() => {
    reportTrend({ months: 6 }).then(setTrend).catch(() => setTrend([]));
  }, [year, month]);

  // Live polling — pauses while tab hidden
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'visible') {
        fetchData(true);
      }
    };
    const timer = setInterval(tick, POLL_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchData(true);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchData]);

  const secondsSinceUpdate = lastUpdated
    ? Math.max(0, Math.floor((Date.now() - lastUpdated.getTime()) / 1000))
    : null;

  const grand = data.branches.reduce(
    (a, b) => ({
      tunai: a.tunai + b.total_tunai,
      debit: a.debit + b.total_debit,
      emoney: a.emoney + b.total_emoney,
      pending: a.pending + b.total_pending,
      selisih: a.selisih + b.total_selisih,
      unbalanced: a.unbalanced + b.unbalanced_count,
    }),
    { tunai: 0, debit: 0, emoney: 0, pending: 0, selisih: 0, unbalanced: 0 }
  );

  const totalDays = data.branches.reduce((a, b) => a + b.days_recorded, 0);

  return (
    <div className="min-h-screen bg-stone-50">
      <AppHeader />

      <main className="max-w-[1400px] mx-auto px-6 md:px-8 py-8 animate-fade-in">
        {/* Page header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold mb-2 flex items-center gap-2">
              <span className="kpi-accent" /> Dashboard Owner
              <span
                className={`inline-flex items-center gap-1.5 ml-2 px-2 py-0.5 rounded-full border text-[10px] font-semibold transition-colors ${
                  justUpdated
                    ? 'bg-lime-100 border-lime-300 text-lime-800'
                    : 'bg-emerald-50 border-emerald-200 text-emerald-800'
                }`}
                data-testid="live-indicator"
                title={lastUpdated ? `Terakhir sinkron: ${lastUpdated.toLocaleTimeString('id-ID')}` : ''}
              >
                <Radio className={`w-2.5 h-2.5 ${justUpdated ? 'text-lime-600' : 'text-emerald-600 animate-pulse'}`} />
                {justUpdated ? 'DATA BARU' : 'LIVE'}
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-semibold text-stone-900 tracking-tight">
              Rekap {MONTHS_ID[month - 1]} <span className="text-emerald-800">{year}</span>
            </h1>
            <p className="text-sm text-stone-500 mt-1 flex items-center gap-3">
              <span>
                {data.branches.length} cabang · {totalDays} hari data
              </span>
              {lastUpdated && (
                <span className="text-stone-400 text-xs" data-testid="last-updated">
                  · terupdate {secondsSinceUpdate < 5 ? 'baru saja' : `${secondsSinceUpdate} detik lalu`}
                </span>
              )}
            </p>
          </div>

          <div className="card-modern p-3 flex items-end gap-3" data-testid="admin-filters">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-stone-600 mb-1 block">Bulan</Label>
              <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
                <SelectTrigger className="bg-white h-9 w-32" data-testid="admin-month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS_ID.map((m, idx) => (
                    <SelectItem key={m} value={String(idx + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-stone-600 mb-1 block">Tahun</Label>
              <Input
                type="number"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value) || now.getFullYear())}
                className="bg-white h-9 w-24"
                data-testid="admin-year-input"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData(false)}
              disabled={loading}
              className="h-9 border-emerald-200 text-emerald-800 hover:bg-emerald-50"
              data-testid="admin-refresh-btn"
              title="Muat ulang manual"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              size="sm"
              onClick={() => navigate(`/kasir?month=${month}&year=${year}`)}
              className="h-9 bg-emerald-800 hover:bg-emerald-900 text-white gap-1.5"
              data-testid="admin-open-kasir-btn"
            >
              <PlusCircle className="w-4 h-4" /> Input Harian
            </Button>
          </div>
        </div>

        {/* Grand totals — hero style */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8 stagger">
          <HeroKpi icon={Building2} label="Cabang Aktif" value={data.branches.length} accent="stone" testid="grand-kpi-0" />
          <HeroKpi icon={Banknote} label="Total Tunai" value={formatRp(grand.tunai)} accent="emerald" testid="grand-kpi-1" />
          <HeroKpi icon={CreditCard} label="Total Debit" value={formatRp(grand.debit)} accent="sky" testid="grand-kpi-2" />
          <HeroKpi icon={Smartphone} label="Total E-Money" value={formatRp(grand.emoney)} accent="amber" testid="grand-kpi-3" />
          <HeroKpi icon={ArrowUpDown} label="Transaksi System (Tunai)" value={formatRp(grand.selisih)} accent={grand.selisih < 0 ? 'rose' : 'emerald'} testid="grand-kpi-4" />
          <HeroKpi icon={AlertTriangle} label="Tidak Balance" value={grand.unbalanced} accent={grand.unbalanced > 0 ? 'rose' : 'stone'} hint={grand.unbalanced > 0 ? 'perhatian' : 'aman'} testid="grand-kpi-5" />
        </div>

        {/* Trend Bulanan — Revenue vs Expense */}
        <div className="card-modern p-5 mb-8" data-testid="trend-chart-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold mb-1">
                JurnalKu · Tren 6 Bulan
              </div>
              <h3 className="font-heading text-lg font-semibold text-stone-900">Pendapatan vs Beban</h3>
            </div>
            <div className="text-[11px] text-stone-500">Data dari jurnal umum</div>
          </div>
          <div className="h-64">
            {trend.length === 0 ? (
              <div className="h-full flex items-center justify-center text-stone-400 text-sm">Belum ada data jurnal.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trend} margin={{ top: 5, right: 8, left: 8, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#57534e' }} axisLine={{ stroke: '#d6d3d1' }} />
                  <YAxis
                    tickFormatter={(v) => v >= 1_000_000 ? `${(v/1_000_000).toFixed(1)}jt` : v >= 1000 ? `${(v/1000).toFixed(0)}rb` : v}
                    tick={{ fontSize: 11, fill: '#57534e' }} axisLine={{ stroke: '#d6d3d1' }} width={60}
                  />
                  <Tooltip
                    formatter={(v) => formatRp(v)}
                    contentStyle={{ background: 'white', border: '1px solid #e7e5e4', borderRadius: 6, fontSize: 12 }}
                    labelStyle={{ fontWeight: 600, color: '#1c1917' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 6 }} iconType="circle" />
                  <Bar dataKey="revenue" name="Pendapatan" fill="#065f46" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expense" name="Beban" fill="#b91c1c" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Section header */}
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">
              Per Cabang
            </div>
            <h2 className="font-heading text-xl font-semibold text-stone-900">
              Ringkasan Kinerja
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/jurnal')}
              className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 text-xs font-semibold transition-colors"
              data-testid="quick-jurnal-btn"
              title="Buka modul JurnalKu (khusus owner)"
            >
              <BookMarked className="w-3.5 h-3.5" /> JurnalKu
              <span className="text-[9px] uppercase tracking-widest text-emerald-700 font-bold border-l border-emerald-200 pl-2 ml-1">
                Owner
              </span>
            </button>
            <div className="text-xs text-stone-500 flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-600" /> Balance
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-600" /> Tidak Balance
              </span>
            </div>
          </div>
        </div>

        {/* Per-branch cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 stagger" data-testid="branch-cards">
          {data.branches.map((b) => {
            const isRed = b.unbalanced_count > 0;
            return (
              <div
                key={b.branch_id}
                className={`card-modern card-lift p-5 overflow-hidden ${isRed ? 'border-red-200' : ''}`}
                data-testid={`branch-card-${b.branch_name}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${isRed ? 'bg-red-50 border border-red-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                      <Building2 className={`w-5 h-5 ${isRed ? 'text-red-700' : 'text-emerald-800'}`} />
                    </div>
                    <div>
                      <div className="font-heading font-semibold text-lg text-stone-900">
                        {b.branch_name}
                      </div>
                      <div className="text-[11px] uppercase tracking-widest text-stone-500">
                        {b.days_recorded} hari · {MONTHS_ID[month - 1]}
                      </div>
                    </div>
                  </div>
                  {isRed ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-900 bg-red-100 border border-red-300 px-2 py-1 rounded">
                      <AlertTriangle className="w-3 h-3" /> {b.unbalanced_count}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded">
                      <ShieldCheck className="w-3 h-3" /> Aman
                    </span>
                  )}
                </div>

                {/* Sparkline of daily tunai */}
                <div className="mb-3 -mx-1">
                  <Sparkline
                    data={b.daily_tunai || []}
                    color={isRed ? '#dc2626' : '#059669'}
                  />
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                  {[
                    ['Tunai', b.total_tunai, Banknote],
                    ['Debit', b.total_debit, CreditCard],
                    ['E-Money', b.total_emoney, Smartphone],
                    ['Pending', b.total_pending, Clock],
                  ].map(([l, v, Ic]) => (
                    <div key={l} className="flex items-center justify-between">
                      <span className="text-stone-500 text-xs flex items-center gap-1.5">
                        <Ic className="w-3 h-3" /> {l}
                      </span>
                      <span className="num text-stone-900 text-xs">{formatRp(v)}</span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between text-sm pt-3 mt-3 border-t border-stone-200">
                  <span className="text-stone-700 font-semibold flex items-center gap-1.5">
                    <ArrowUpDown className="w-3.5 h-3.5" /> Transaksi System (Tunai)
                  </span>
                  <span className={`num font-semibold ${b.total_selisih < 0 ? 'text-red-700' : 'text-emerald-800'}`}>
                    {formatRp(b.total_selisih)}
                  </span>
                </div>

                <button
                  onClick={() => navigate(`/kasir?branch=${b.branch_id}&month=${month}&year=${year}`)}
                  className="mt-3 w-full flex items-center justify-between px-3 py-2 rounded-md border border-stone-200 bg-stone-50 hover:bg-emerald-50 hover:border-emerald-200 group transition-colors text-sm text-stone-700 hover:text-emerald-900"
                  data-testid={`open-kasir-${b.branch_name}`}
                >
                  <span className="font-medium">Buka Input Harian</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </button>
              </div>
            );
          })}
          {data.branches.length === 0 && !loading && (
            <div className="col-span-full card-modern p-10 text-center">
              <Users2 className="w-8 h-8 text-stone-400 mx-auto mb-2" />
              <div className="text-stone-500 text-sm">Belum ada cabang yang bisa dilihat.</div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
